import type { LabelCorner } from "./labelDetectionWorkerService";
import type { VisionProductionBundle } from "./visualLabService";

type TensorPayload = { shape: number[]; data: number[] };
type CnnCalibrationAnchor = { uncertainty: number; expected_corner_error: number; sample_count?: number };
type CnnConfidenceCalibration = {
  version?: string;
  source_split?: string;
  error_scale?: number;
  global_corner_error?: number;
  anchors?: CnnCalibrationAnchor[];
};
type CnnUncertaintyConfig = {
  version?: string;
  method?: string;
  brightness_factors?: number[];
};
type WebModelPayload = {
  schema_version: number;
  runtime: "tiny-corner-cnn-js-v1" | "tiny-corner-cnn-js-v2-canonical-input" | "tiny-corner-cnn-js-v3-tta-uncertainty";
  architecture: string;
  input_size: [number, number];
  validation_corner_error: number | null;
  input_transform?: "canonical-gray-bilinear-half-pixel-v1" | string;
  uncertainty?: CnnUncertaintyConfig | null;
  confidence_calibration?: CnnConfidenceCalibration | null;
  weights: Record<string, TensorPayload>;
};
type Tensor3 = { data: Float32Array; channels: number; height: number; width: number };
export type CnnWebInferenceResult = {
  corners: LabelCorner[];
  confidence: number;
  geometryScore: number;
  runtimeMs: number;
  inputWidth: number;
  inputHeight: number;
  perImageUncertainty: number | null;
  ttaMaxCornerDelta: number | null;
  ttaPasses: number;
  expectedCornerError: number | null;
  calibrationVersion: string | null;
  calibrationSource: string | null;
};

const MODEL_CACHE = "materialentnahme-cnn-models-v1";
const MODEL_CACHE_INDEX_KEY = "materialentnahme:cnn-model-cache-index:v1";
let memory: { bundleId: string; webModelSha256: string; model: WebModelPayload } | null = null;
let lastIntegrityWarningKey: string | null = null;

function readModelCacheIndex(): Record<string, number> {
  if (typeof window === "undefined") return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(MODEL_CACHE_INDEX_KEY) ?? "{}") as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const index: Record<string, number> = {};
    for (const [bundleId, stamp] of Object.entries(value as Record<string, unknown>)) {
      if (typeof stamp === "number" && Number.isFinite(stamp)) index[bundleId] = stamp;
    }
    return index;
  } catch { return {}; }
}

function touchModelCache(bundleId: string): void {
  if (typeof window === "undefined") return;
  const index = readModelCacheIndex();
  index[bundleId] = Date.now();
  try { window.localStorage.setItem(MODEL_CACHE_INDEX_KEY, JSON.stringify(index)); } catch { /* optional */ }
}

function cachedBundleId(request: Request): string | null {
  try {
    const pathname = new URL(request.url).pathname;
    const match = pathname.match(/^\/__materialentnahme_cnn__\/(.+)\.json$/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch { return null; }
}

export async function pruneCnnWebModelCache(maxModels = 2): Promise<number> {
  if (typeof window === "undefined" || !("caches" in window)) return 0;
  const cache = await caches.open(MODEL_CACHE);
  const keys = await cache.keys();
  if (keys.length <= Math.max(1, maxModels)) return 0;
  const index = readModelCacheIndex();
  const ranked = keys.map((request) => ({
    request,
    bundleId: cachedBundleId(request),
    stamp: index[cachedBundleId(request) ?? ""] ?? 0,
  })).sort((a, b) => {
    if (a.bundleId && a.bundleId === memory?.bundleId) return -1;
    if (b.bundleId && b.bundleId === memory?.bundleId) return 1;
    return b.stamp - a.stamp;
  });
  const keep = new Set(ranked.slice(0, Math.max(1, maxModels)).map((item) => item.request.url));
  let removed = 0;
  for (const item of ranked) {
    if (keep.has(item.request.url)) continue;
    if (await cache.delete(item.request)) removed += 1;
    if (item.bundleId) delete index[item.bundleId];
  }
  try { window.localStorage.setItem(MODEL_CACHE_INDEX_KEY, JSON.stringify(index)); } catch { /* optional */ }
  return removed;
}
const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));

function normalizedSha256(value: string | null | undefined): string | null {
  const normalized = String(value ?? "").trim().toLowerCase();
  return /^[a-f0-9]{64}$/.test(normalized) ? normalized : null;
}

async function sha256Hex(payload: ArrayBuffer): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error("Der Browser unterstützt keine SHA-256-Prüfung für das Production-CNN.");
  const digest = await globalThis.crypto.subtle.digest("SHA-256", payload);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

function reportIntegrityFallbackOnce(bundle: VisionProductionBundle, detail: string): void {
  if (typeof window === "undefined") return;
  const key = `${bundle.bundle_id}:${detail}`;
  if (lastIntegrityWarningKey === key) return;
  lastIntegrityWarningKey = key;
  window.dispatchEvent(new CustomEvent("materialentnahme:toast", {
    detail: { message: "CNN-Modellintegrität konnte nicht bestätigt werden. Sicherer OpenCV-Fallback ist aktiv." },
  }));
}

async function verifyWebModelResponse(response: Response, bundle: VisionProductionBundle): Promise<string> {
  const expected = normalizedSha256(bundle.cnn?.web_model_sha256);
  if (!expected) {
    throw new Error("Production-Bundle enthält keine gültige CNN-Webmodell-SHA-256.");
  }
  const actual = await sha256Hex(await response.clone().arrayBuffer());
  if (actual !== expected) {
    throw new Error(`CNN-Webmodell-SHA-256 stimmt nicht (${actual.slice(0, 12)} != ${expected.slice(0, 12)}).`);
  }
  return expected;
}

function parseWebModelResponse(response: Response): Promise<WebModelPayload> {
  return response.json() as Promise<WebModelPayload>;
}

function validateWebModel(model: WebModelPayload): void {
  if (![
    "tiny-corner-cnn-js-v1",
    "tiny-corner-cnn-js-v2-canonical-input",
    "tiny-corner-cnn-js-v3-tta-uncertainty",
  ].includes(model.runtime) || !model.weights) throw new Error("CNN-Webmodell ist mit dieser PWA-Version nicht kompatibel.");
  if (model.runtime !== "tiny-corner-cnn-js-v1" && model.input_transform !== "canonical-gray-bilinear-half-pixel-v1") {
    throw new Error("CNN-Input-Transform ist mit dieser PWA-Version nicht kompatibel.");
  }
  if (model.runtime === "tiny-corner-cnn-js-v3-tta-uncertainty" && (!model.uncertainty || !model.confidence_calibration)) {
    throw new Error("CNN-2.0-Modell enthält keine vollständige Uncertainty-Kalibrierung.");
  }
}

async function loadModel(bundle: VisionProductionBundle): Promise<WebModelPayload> {
  const expectedSha256 = normalizedSha256(bundle.cnn?.web_model_sha256);
  if (!expectedSha256) {
    const detail = "Production-Bundle enthält keine gültige CNN-Webmodell-SHA-256.";
    reportIntegrityFallbackOnce(bundle, detail);
    throw new Error(detail);
  }
  if (memory?.bundleId === bundle.bundle_id && memory.webModelSha256 === expectedSha256) {
    touchModelCache(bundle.bundle_id);
    return memory.model;
  }

  const synthetic = new Request(`/__materialentnahme_cnn__/${encodeURIComponent(bundle.bundle_id)}.json`);
  let cachedFailure: unknown;

  // Production ist offline-first: zuerst den bereits lokal vorhandenen, SHA-verifizierten
  // immutable Bundle-Cache benutzen. Ein korrupter Cacheeintrag wird entfernt und
  // anschließend einmal live nachgeladen. Es gibt bewusst keinen negativen Promise-Cache.
  if ("caches" in window) {
    const cache = await caches.open(MODEL_CACHE);
    const cached = await cache.match(synthetic);
    if (cached) {
      try {
        await verifyWebModelResponse(cached, bundle);
        const model = await parseWebModelResponse(cached.clone());
        validateWebModel(model);
        memory = { bundleId: bundle.bundle_id, webModelSha256: expectedSha256, model };
        touchModelCache(bundle.bundle_id);
        return model;
      } catch (error) {
        await cache.delete(synthetic);
        cachedFailure = error;
      }
    }
  }

  let liveFailure: unknown;
  try {
    const live = await fetch(bundle.model_url, { credentials: "include", cache: "no-store" });
    if (live.ok) {
      await verifyWebModelResponse(live, bundle);
      const model = await parseWebModelResponse(live.clone());
      validateWebModel(model);
      if ("caches" in window) {
        const cache = await caches.open(MODEL_CACHE);
        await cache.put(synthetic, live.clone());
      }
      memory = { bundleId: bundle.bundle_id, webModelSha256: expectedSha256, model };
      touchModelCache(bundle.bundle_id);
      return model;
    }
    liveFailure = new Error(`CNN-Webmodell konnte nicht geladen werden (HTTP ${live.status}).`);
  } catch (error) {
    liveFailure = error;
  }

  const failure = liveFailure ?? cachedFailure;
  if (failure instanceof Error) {
    reportIntegrityFallbackOnce(bundle, failure.message);
    throw failure;
  }
  const unavailable = new Error("Das freigegebene CNN-Modell ist lokal noch nicht verfügbar.");
  reportIntegrityFallbackOnce(bundle, unavailable.message);
  throw unavailable;
}


export async function ensureProductionCnnBundleCached(bundle: VisionProductionBundle): Promise<void> {
  if (!bundle.cnn?.model_id) return;
  await loadModel(bundle);
}

function resizeGray(image: ImageData, width: number, height: number): Tensor3 {
  const canvas = document.createElement("canvas"); canvas.width = image.width; canvas.height = image.height;
  const ctx = canvas.getContext("2d"); if (!ctx) throw new Error("CNN-Bild konnte nicht vorbereitet werden."); ctx.putImageData(image, 0, 0);
  const out = document.createElement("canvas"); out.width = width; out.height = height; const outContext = out.getContext("2d", { willReadFrequently: true }); if (!outContext) throw new Error("CNN-Bild konnte nicht skaliert werden.");
  outContext.drawImage(canvas, 0, 0, width, height); const rgba = outContext.getImageData(0, 0, width, height).data; const data = new Float32Array(width * height);
  for (let index = 0, target = 0; index < rgba.length; index += 4, target += 1) data[target] = (0.299 * rgba[index] + 0.587 * rgba[index + 1] + 0.114 * rgba[index + 2]) / 255;
  return { data, channels: 1, height, width };
}

function resizeGrayCanonical(image: ImageData, width: number, height: number): Tensor3 {
  const srcWidth = image.width, srcHeight = image.height;
  if (srcWidth < 1 || srcHeight < 1 || width < 1 || height < 1) throw new Error("CNN-Bildgröße ist ungültig.");
  const gray = new Float64Array(srcWidth * srcHeight);
  for (let index = 0, target = 0; index < image.data.length; index += 4, target += 1) gray[target] = (0.299 * image.data[index] + 0.587 * image.data[index + 1] + 0.114 * image.data[index + 2]) / 255;
  const data = new Float32Array(width * height), scaleX = srcWidth / width, scaleY = srcHeight / height;
  for (let oy = 0; oy < height; oy += 1) {
    const sy = Math.max(0, Math.min(srcHeight - 1, (oy + .5) * scaleY - .5)), y0 = Math.floor(sy), y1 = Math.min(srcHeight - 1, y0 + 1), wy = sy - y0;
    for (let ox = 0; ox < width; ox += 1) {
      const sx = Math.max(0, Math.min(srcWidth - 1, (ox + .5) * scaleX - .5)), x0 = Math.floor(sx), x1 = Math.min(srcWidth - 1, x0 + 1), wx = sx - x0;
      const top = gray[y0 * srcWidth + x0] * (1 - wx) + gray[y0 * srcWidth + x1] * wx;
      const bottom = gray[y1 * srcWidth + x0] * (1 - wx) + gray[y1 * srcWidth + x1] * wx;
      data[oy * width + ox] = top * (1 - wy) + bottom * wy;
    }
  }
  return { data, channels: 1, height, width };
}

function tensor(model: WebModelPayload, key: string): TensorPayload { const value = model.weights[key]; if (!value) throw new Error(`CNN-Gewicht fehlt: ${key}`); return value; }
function conv2d(input: Tensor3, weights: TensorPayload, bias: TensorPayload, stride: number, pad: number): Tensor3 {
  const [outChannels, inChannels, kernelHeight, kernelWidth] = weights.shape; const outHeight = Math.floor((input.height + 2 * pad - kernelHeight) / stride) + 1; const outWidth = Math.floor((input.width + 2 * pad - kernelWidth) / stride) + 1; const out = new Float32Array(outChannels * outHeight * outWidth);
  for (let outChannel = 0; outChannel < outChannels; outChannel += 1) for (let oy = 0; oy < outHeight; oy += 1) for (let ox = 0; ox < outWidth; ox += 1) { let sum = bias.data[outChannel] ?? 0; for (let inChannel = 0; inChannel < inChannels; inChannel += 1) for (let ky = 0; ky < kernelHeight; ky += 1) { const iy = oy * stride + ky - pad; if (iy < 0 || iy >= input.height) continue; for (let kx = 0; kx < kernelWidth; kx += 1) { const ix = ox * stride + kx - pad; if (ix < 0 || ix >= input.width) continue; sum += input.data[(inChannel * input.height + iy) * input.width + ix] * weights.data[(((outChannel * inChannels + inChannel) * kernelHeight + ky) * kernelWidth + kx)]; } } out[(outChannel * outHeight + oy) * outWidth + ox] = Math.max(0, sum); }
  return { data: out, channels: outChannels, height: outHeight, width: outWidth };
}
function maxPool2(input: Tensor3): Tensor3 { const outHeight = Math.floor(input.height / 2), outWidth = Math.floor(input.width / 2), out = new Float32Array(input.channels * outHeight * outWidth); for (let channel = 0; channel < input.channels; channel += 1) for (let y = 0; y < outHeight; y += 1) for (let x = 0; x < outWidth; x += 1) { let maximum = -Infinity; for (let dy = 0; dy < 2; dy += 1) for (let dx = 0; dx < 2; dx += 1) maximum = Math.max(maximum, input.data[(channel * input.height + y * 2 + dy) * input.width + x * 2 + dx]); out[(channel * outHeight + y) * outWidth + x] = maximum; } return { data: out, channels: input.channels, height: outHeight, width: outWidth }; }
function adaptiveAvg(input: Tensor3, outHeight = 4, outWidth = 6): Float32Array { const out = new Float32Array(input.channels * outHeight * outWidth); for (let channel = 0; channel < input.channels; channel += 1) for (let oy = 0; oy < outHeight; oy += 1) { const y0 = Math.floor(oy * input.height / outHeight), y1 = Math.ceil((oy + 1) * input.height / outHeight); for (let ox = 0; ox < outWidth; ox += 1) { const x0 = Math.floor(ox * input.width / outWidth), x1 = Math.ceil((ox + 1) * input.width / outWidth); let sum = 0, count = 0; for (let y = y0; y < y1; y += 1) for (let x = x0; x < x1; x += 1) { sum += input.data[(channel * input.height + y) * input.width + x]; count += 1; } out[(channel * outHeight + oy) * outWidth + ox] = count ? sum / count : 0; } } return out; }
function linear(input: Float32Array, weights: TensorPayload, bias: TensorPayload, relu: boolean): Float32Array { const [outFeatures, inFeatures] = weights.shape, out = new Float32Array(outFeatures); for (let output = 0; output < outFeatures; output += 1) { let sum = bias.data[output] ?? 0; const base = output * inFeatures; for (let index = 0; index < inFeatures; index += 1) sum += input[index] * weights.data[base + index]; out[output] = relu ? Math.max(0, sum) : 1 / (1 + Math.exp(-sum)); } return out; }
function inferTensor(model: WebModelPayload, input: Tensor3): number[] { let value = input; value = conv2d(value, tensor(model, "features.0.weight"), tensor(model, "features.0.bias"), 2, 2); value = maxPool2(value); value = conv2d(value, tensor(model, "features.3.weight"), tensor(model, "features.3.bias"), 2, 1); value = conv2d(value, tensor(model, "features.5.weight"), tensor(model, "features.5.bias"), 2, 1); const pooled = adaptiveAvg(value); const hidden = linear(pooled, tensor(model, "head.1.weight"), tensor(model, "head.1.bias"), true); return Array.from(linear(hidden, tensor(model, "head.4.weight"), tensor(model, "head.4.bias"), false)); }
function geometryScore(points: LabelCorner[]): number { let area = 0; for (let index = 0; index < 4; index += 1) { const next = points[(index + 1) % 4]; area += points[index].x * next.y - next.x * points[index].y; } area = Math.abs(area) * .5; const ordering = points[0].x < points[1].x && points[3].x < points[2].x && points[0].y < points[3].y && points[1].y < points[2].y; return clamp01(area / .25) * (ordering ? 1 : .45); }

function brightnessVariant(input: Tensor3, factor: number): Tensor3 {
  const data = new Float32Array(input.data.length);
  for (let index = 0; index < input.data.length; index += 1) data[index] = clamp01(input.data[index] * factor);
  return { ...input, data };
}

function summarizePredictions(predictions: number[][]): { corners: LabelCorner[]; uncertainty: number; maxCornerDelta: number } {
  const corners: LabelCorner[] = [];
  for (let corner = 0; corner < 4; corner += 1) {
    let x = 0, y = 0;
    for (const prediction of predictions) { x += prediction[corner * 2]; y += prediction[corner * 2 + 1]; }
    corners.push({ x: clamp01(x / predictions.length), y: clamp01(y / predictions.length) });
  }
  const distances: number[] = [];
  for (const prediction of predictions) for (let corner = 0; corner < 4; corner += 1) distances.push(Math.hypot(prediction[corner * 2] - corners[corner].x, prediction[corner * 2 + 1] - corners[corner].y));
  return { corners, uncertainty: distances.reduce((sum, value) => sum + value, 0) / Math.max(1, distances.length), maxCornerDelta: distances.length ? Math.max(...distances) : 0 };
}

function expectedCornerError(uncertainty: number, calibration: CnnConfidenceCalibration | null | undefined, fallback: number | null): number {
  const fallbackError = typeof fallback === "number" && Number.isFinite(fallback) ? Math.max(0, fallback) : .125;
  const anchors = Array.isArray(calibration?.anchors) ? calibration.anchors.map((anchor) => ({ x: Number(anchor.uncertainty), y: Number(anchor.expected_corner_error) })).filter((anchor) => Number.isFinite(anchor.x) && Number.isFinite(anchor.y)).sort((a, b) => a.x - b.x) : [];
  if (!anchors.length) return typeof calibration?.global_corner_error === "number" && Number.isFinite(calibration.global_corner_error) ? Math.max(0, calibration.global_corner_error) : fallbackError;
  const value = Math.max(0, uncertainty);
  if (value <= anchors[0].x) return Math.max(0, anchors[0].y);
  for (let index = 1; index < anchors.length; index += 1) {
    const left = anchors[index - 1], right = anchors[index];
    if (value <= right.x) { const width = right.x - left.x; if (width <= 1e-12) return Math.max(left.y, right.y); const ratio = (value - left.x) / width; return left.y * (1 - ratio) + right.y * ratio; }
  }
  const last = anchors[anchors.length - 1]; return last.y + Math.max(0, value - last.x) * 1.5;
}

export async function inferProductionCnn(image: ImageData, bundle: VisionProductionBundle): Promise<CnnWebInferenceResult> {
  const started = performance.now(); const model = await loadModel(bundle); const scale = Math.max(.5, Math.min(1.5, bundle.hybrid.input_scale || 1)); const width = Math.max(64, Math.round(model.input_size[0] * scale)), height = Math.max(48, Math.round(model.input_size[1] * scale));
  const input = model.runtime === "tiny-corner-cnn-js-v1" ? resizeGray(image, width, height) : resizeGrayCanonical(image, width, height);

  if (model.runtime === "tiny-corner-cnn-js-v3-tta-uncertainty") {
    const factors = (Array.isArray(model.uncertainty?.brightness_factors) && model.uncertainty.brightness_factors.length ? model.uncertainty.brightness_factors : [1, .85, 1.15]).slice(0, 5).map((factor) => clamp(Number(factor), .5, 1.5));
    const predictions = factors.map((factor) => inferTensor(model, brightnessVariant(input, factor)));
    const summary = summarizePredictions(predictions); const geometry = geometryScore(summary.corners); const expected = expectedCornerError(summary.uncertainty, model.confidence_calibration, model.validation_corner_error); const errorScale = Math.max(.05, Number(model.confidence_calibration?.error_scale) || .25); const errorConfidence = clamp01(1 - expected / errorScale); const confidence = Math.min(.98, clamp01(errorConfidence * (.85 + .15 * geometry)));
    return { corners: summary.corners, confidence, geometryScore: geometry, runtimeMs: Math.round((performance.now() - started) * 10) / 10, inputWidth: width, inputHeight: height, perImageUncertainty: summary.uncertainty, ttaMaxCornerDelta: summary.maxCornerDelta, ttaPasses: predictions.length, expectedCornerError: expected, calibrationVersion: model.confidence_calibration?.version ?? null, calibrationSource: model.confidence_calibration?.source_split ?? null };
  }

  const prediction = inferTensor(model, input); const corners: LabelCorner[] = []; for (let index = 0; index < 4; index += 1) corners.push({ x: clamp01(prediction[index * 2]), y: clamp01(prediction[index * 2 + 1]) }); const geometry = geometryScore(corners); const error = model.validation_corner_error; const modelConfidence = typeof error === "number" ? clamp01(1 - error / .25) : .5; const confidence = Math.min(.95, clamp01(modelConfidence * .75 + geometry * .25));
  return { corners, confidence, geometryScore: geometry, runtimeMs: Math.round((performance.now() - started) * 10) / 10, inputWidth: width, inputHeight: height, perImageUncertainty: null, ttaMaxCornerDelta: null, ttaPasses: 1, expectedCornerError: typeof error === "number" ? error : null, calibrationVersion: null, calibrationSource: null };
}
